import get from 'lodash/get';
import store from '../..';
import {
  ContentType,
  operationType,
  REGENERATION_LIMIT,
  summarizationStatuses,
} from '../../../../constants/common';
import { fetchRegeneratedContent } from '../../../../services/Content';
import {
  ModuleComponentsPayload,
  ModuleComponentType,
} from '../../../../types/Content';
import { ApplicableRecommendation } from '../../../../types/contentValidation';
import {
  MarketingFormFieldRequest,
  SecondaryFormApiField,
  VersionPayload,
} from '../../../../types/MarketingBox';
import { ParamsUsed } from '../../../../types/store/markettingContentGeneration/majorProcessActions';
import { getContentGroupId, getSessionId } from '../../../../utils/commonUtils';
import {
  getCurrentSelectedLanguage,
  getGenerateContentPayload,
  getParamsUsed,
  getParentId,
  getRefinementAdditionalParams,
  getVersionIds,
  resetTranslationState,
  getEncodedUserNtid,
} from '../../helpers/common';
import { contentListStateSegment } from '../../stateSegments/contentListSegment';
import { moduleComponentItemsStateSegment } from '../../stateSegments/ModuleComponentItemsStateSegment';
import { DSPGeneratedContent } from '../../../../components/molecules/DSPRenderer/DSPRenderer.types';
import { moduleContentsStateSegment } from '../../stateSegments/emailModulesStateSegment';
import { getGeneratedContentValidations } from './getGeneratedContentValidation';
import { GenerateContentValidationStateSegment } from '../../stateSegments/GenerateContentValidationStateSegment';
import {
  trackBoostContentInitiate,
  trackBoostContentAppliedRecommendationCount,
  trackBoostContentComplete,
  trackBoostContentError,
} from '../../../../services/Analytics';

export const boostContent = async (
  paramsUsed: ParamsUsed,
  currentContentId?: string
) => {
  resetTranslationState();

  // Track boost content initiate analytics
  trackBoostContentInitiate();

  const userEncodedNtid = getEncodedUserNtid();
  const currentSelectedLanguage = getCurrentSelectedLanguage();
  const generatedContentPayload = getGenerateContentPayload(
    paramsUsed,
    undefined,
    currentContentId
  );
  const refinePayload = getRefinementAdditionalParams(paramsUsed);
  const applyRecommendationsPayload =
    paramsUsed.currentSelectedApplicableRecommendation;

  const parentId = getParentId();
  const versionIds = getVersionIds();

  type RegenerateContentPayload = MarketingFormFieldRequest &
    SecondaryFormApiField &
    VersionPayload & { sessionId: string } & {
      applyRecommendations: ApplicableRecommendation[];
    };
  const requestPayload: RegenerateContentPayload = {
    ...generatedContentPayload,
    ...refinePayload,
    applyRecommendations: applyRecommendationsPayload,
    sessionId: getSessionId(),
    contentGroupId: getContentGroupId(),
    parentId,
    versionIds,
    operationType: operationType.BOOST,
    ...(currentContentId && { contentId: currentContentId }),
  };

  store.set(contentListStateSegment, prev => {
    return {
      ...prev,
      isReGeneratingContent: true,
      retryPromptParams: paramsUsed,
    };
  });

  if (
    store.get(moduleComponentItemsStateSegment) &&
    store.get(moduleComponentItemsStateSegment).length > 0
  ) {
    const nameMap: Record<string, string> = {
      HEADING: 'heading',
      SUBHEADING: 'subHeading',
      MESSAGE: 'message',
      CALL_TO_ACTION: 'callToAction',
    };
    const moduleData: ModuleComponentsPayload = {
      generateComponents: store
        .get(moduleComponentItemsStateSegment)
        .map(item => item.id),
      deleteComponents: [],
      components: store
        .get(moduleComponentItemsStateSegment)
        .map((item, index) => {
          return {
            id: item.id,
            name: nameMap[item.type],
            code: item.type,
            sequence: index,
          } as ModuleComponentType;
        }),
    };

    requestPayload.moduleData = moduleData;
  }

  const { success, data } = await fetchRegeneratedContent(requestPayload);

  const contentId = get(data, 'id', '');
  const summarizationStatus = (data?.content as DSPGeneratedContent)
    ?.contentSummary?.summarizationStatus;

  const isDspError =
    requestPayload.contentType === ContentType.DSP &&
    summarizationStatus === summarizationStatuses.SUMMARIZATION_ERROR;

  if (isDspError) {
    // Track boost content error
    trackBoostContentError();

    store.set(contentListStateSegment, prev => {
      return {
        ...prev,
        isReGeneratingContent: false,
        errorReGeneratingContent: true,
        errorMessageGeneratingContent:
          'There has been an error in generating content',
      };
    });

    return;
  }

  if (success) {
    const { contentType, destinationFormat, parentId: id } = requestPayload;

    if (
      contentType === 'email' ||
      contentType?.toLowerCase() === 'rte' ||
      destinationFormat?.toLowerCase() === 'email' ||
      destinationFormat?.toLowerCase() === 'rte'
    ) {
      const moduleContents = new Map(
        Array.from(store.get(moduleContentsStateSegment))?.values()
      );

      const moduleContent = Array.from(moduleContents.values())?.find(
        module => module?.parentContentId === id
      );

      if (moduleContent) {
        const { mapKey, refineConfig, translationConfig, generatedContents } =
          moduleContent || {};

        const wasLastContentRefined =
          refineConfig?.[Object.keys(refineConfig).length - 1]?.isRefineApplied;

        const refineConfigOption = {
          isRefineApplied: wasLastContentRefined,
          operationType: requestPayload?.operationType,
          inclusions: requestPayload?.inclusions,
          exclusions: requestPayload?.exclusions,
          contentStyle: requestPayload?.contentStyle,
        };

        moduleContents.set(mapKey, {
          ...moduleContent,
          translationConfig: {
            ...translationConfig,
            [Object.keys(translationConfig)?.length]: currentSelectedLanguage,
          },
          refineConfig: {
            ...refineConfig,
            [Object.keys(refineConfig)?.length]: refineConfigOption,
          },
          generatedContents: {
            ...generatedContents,
            [Object.keys(generatedContents)?.length]: {
              contentId,
            },
          },
        });

        store.set(moduleContentsStateSegment, moduleContents);
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    store.set(contentListStateSegment, (prev: any) => {
      const wasLastContentRefined =
        prev?.contentList?.[prev.contentList.length - 1]?.isRefineApplied;

      const content = {
        [currentSelectedLanguage]: data,
        paramsUsed: {
          ...getParamsUsed(paramsUsed),
          applyRecommendations: applyRecommendationsPayload,
        },
        regenerationLimit: REGENERATION_LIMIT - 1,
        apiPayloadUsed: requestPayload,
        isRefineApplied: wasLastContentRefined,
        isRecommendationApplied: true,
      };

      return {
        ...prev,
        isReGeneratingContent: false,
        errorReGeneratingContent: false,
        errorMessage: '',
        isGeneratingContent: false,
        contentList: [...prev.contentList, content] as never,
      };
    });

    // Track applied recommendation count analytics
    if (applyRecommendationsPayload?.length > 0) {
      trackBoostContentAppliedRecommendationCount(
        applyRecommendationsPayload.length,
        userEncodedNtid
      );
    }

    // Track boost content completion
    trackBoostContentComplete();

    store.set(GenerateContentValidationStateSegment, prev => {
      // Update the previous content's applicable recommendations to mark them as selected
      // Get the parentId from the last element of versionIds array
      const previousContentId = versionIds?.[versionIds.length - 1];

      if (
        previousContentId &&
        prev[previousContentId] &&
        applyRecommendationsPayload?.length > 0
      ) {
        const updatedApplicableRecommendations = prev[
          previousContentId
        ].applicableRecommendations.map(recommendation => {
          const isSelectedRecommendation = applyRecommendationsPayload.some(
            appliedRec =>
              appliedRec.recommendationId === recommendation.recommendationId
          );
          // Preserve existing selections while adding new ones
          return {
            ...recommendation,
            isSelected: isSelectedRecommendation || recommendation.isSelected,
          };
        });

        return {
          ...prev,
          [previousContentId]: {
            ...prev[previousContentId],
            applicableRecommendations: updatedApplicableRecommendations,
          },
        };
      }

      return prev;
    });

    // fetch generated content validation
    getGeneratedContentValidations({
      contentId,
      action: get(generatedContentPayload, 'action', ''),
      sessionId: getSessionId(),
      contentGroupId: getContentGroupId(),
    });
  } else {
    // Track boost content error
    trackBoostContentError();

    store.set(contentListStateSegment, prev => {
      return {
        ...prev,
        isReGeneratingContent: false,
        errorReGeneratingContent: true,
        errorMessageGeneratingContent:
          'There has been an error in generating content',
      };
    });
  }
};
